import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Clock, Edit2, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';

const UTASchedules = ({ user }) => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [formData, setFormData] = useState({
    uta_name: '',
    season: 'inverno',
    days_of_week: [],  // Array for multiple days
    turn_on_time: '',
    turn_off_time: '',
    turn_on_time_2: '',
    turn_off_time_2: ''
  });

  const utaList = [
    'Impianto riscaldamento UTA piscine 15m e 25m',
    'Impianto riscaldamento UTA piscina 20m',
    'Impianto riscaldamento UTA spogliatoi',
    'Impianto riscaldamento UTA palestra',
    'Impianto riscaldamento uffici'
  ];

  const daysOfWeek = ['lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato', 'domenica'];

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    try {
      const response = await axiosInstance.get('/uta-schedules');
      setSchedules(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento degli orari');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation: at least one day must be selected for creation
    if (!editingSchedule && formData.days_of_week.length === 0) {
      toast.error('Seleziona almeno un giorno');
      return;
    }

    try {
      if (editingSchedule) {
        // For editing, send single day_of_week (not array)
        const updateData = {
          ...formData,
          day_of_week: formData.days_of_week[0]  // Use first day from array
        };
        delete updateData.days_of_week;
        await axiosInstance.put(`/uta-schedules/${editingSchedule.id}`, updateData);
        toast.success('Orario aggiornato!');
      } else {
        // For creation, send days_of_week array
        await axiosInstance.post('/uta-schedules', formData);
        const dayCount = formData.days_of_week.length;
        toast.success(`${dayCount} orario${dayCount > 1 ? 'i creati' : ' creato'}!`);
      }
      fetchSchedules();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      uta_name: schedule.uta_name,
      season: schedule.season,
      days_of_week: [schedule.day_of_week],  // Convert single day to array
      turn_on_time: schedule.turn_on_time,
      turn_off_time: schedule.turn_off_time,
      turn_on_time_2: schedule.turn_on_time_2 || '',
      turn_off_time_2: schedule.turn_off_time_2 || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (schedule) => {
    if (!window.confirm(`Eliminare l'orario per ${schedule.uta_name} (${schedule.season} - ${schedule.day_of_week})?`)) {
      return;
    }

    try {
      await axiosInstance.delete(`/uta-schedules/${schedule.id}`);
      toast.success('Orario eliminato!');
      fetchSchedules();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const resetForm = () => {
    setFormData({
      uta_name: '',
      season: 'inverno',
      days_of_week: [],
      turn_on_time: '',
      turn_off_time: '',
      turn_on_time_2: '',
      turn_off_time_2: ''
    });
    setEditingSchedule(null);
  };

  const toggleDay = (day) => {
    setFormData(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day)
        ? prev.days_of_week.filter(d => d !== day)
        : [...prev.days_of_week, day]
    }));
  };

  const getSchedulesBySeason = (season) => {
    return schedules.filter(s => s.season === season);
  };

  const getSchedulesBySeasonAndDay = (season, day) => {
    return schedules.filter(s => s.season === season && s.day_of_week === day);
  };

  const canEdit = user?.role === 'admin' || user?.role === 'maintainer';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-xl text-gray-600">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Orari Riscaldamento</h1>
          <p className="text-gray-600">Gestione orari di accensione e spegnimento</p>
        </div>
        {canEdit && (
          <Button
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Orario
          </Button>
        )}
      </div>

      {/* Dialog per creare/modificare orari */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSchedule ? 'Modifica Orario' : 'Nuovo Orario'}</DialogTitle>
            <DialogDescription>
              Imposta gli orari di accensione e spegnimento
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="uta_name">UTA</Label>
              <select
                id="uta_name"
                value={formData.uta_name}
                onChange={(e) => setFormData({ ...formData, uta_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Seleziona UTA</option>
                {utaList.map((uta) => (
                  <option key={uta} value={uta}>{uta}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="season">Stagione</Label>
              <select
                id="season"
                value={formData.season}
                onChange={(e) => setFormData({ ...formData, season: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="inverno">Inverno</option>
                <option value="estate">Estate</option>
              </select>
            </div>
            <div>
              <Label>Giorni della settimana {!editingSchedule && <span className="text-red-500">*</span>}</Label>
              {editingSchedule && (
                <p className="text-xs text-gray-500 mb-2">In modalità modifica, il giorno non può essere cambiato. Elimina e crea un nuovo orario se necessario.</p>
              )}
              <div className="grid grid-cols-2 gap-2 mt-2">
                {daysOfWeek.map((day) => (
                  <div
                    key={day}
                    onClick={() => !editingSchedule && toggleDay(day)}
                    className={`flex items-center px-3 py-2 border rounded-md transition-colors ${
                      formData.days_of_week.includes(day)
                        ? 'bg-blue-50 border-blue-500'
                        : 'border-gray-300 hover:bg-gray-50'
                    } ${editingSchedule ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.days_of_week.includes(day)}
                      onChange={() => {}}
                      disabled={editingSchedule}
                      className="mr-2 pointer-events-none"
                      readOnly
                    />
                    <span className="text-sm capitalize">{day}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Prima fascia oraria */}
            <div className="border-t pt-3">
              <h4 className="font-medium mb-3">Prima fascia oraria</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="turn_on_time">Accensione</Label>
                  <Input
                    id="turn_on_time"
                    type="time"
                    value={formData.turn_on_time}
                    onChange={(e) => setFormData({ ...formData, turn_on_time: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="turn_off_time">Spegnimento</Label>
                  <Input
                    id="turn_off_time"
                    type="time"
                    value={formData.turn_off_time}
                    onChange={(e) => setFormData({ ...formData, turn_off_time: e.target.value })}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Seconda fascia oraria (opzionale) */}
            <div className="border-t pt-3">
              <h4 className="font-medium mb-1">Seconda fascia oraria (opzionale)</h4>
              <p className="text-xs text-gray-500 mb-3">Lascia vuoto se non serve una seconda accensione</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="turn_on_time_2">Accensione</Label>
                  <Input
                    id="turn_on_time_2"
                    type="time"
                    value={formData.turn_on_time_2}
                    onChange={(e) => setFormData({ ...formData, turn_on_time_2: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="turn_off_time_2">Spegnimento</Label>
                  <Input
                    id="turn_off_time_2"
                    type="time"
                    value={formData.turn_off_time_2}
                    onChange={(e) => setFormData({ ...formData, turn_off_time_2: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
            >
              {editingSchedule ? 'Aggiorna Orario' : 'Crea Orario'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Tabella Inverno */}
      <Card className="shadow-md border-0">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Clock className="w-5 h-5 mr-2 text-blue-600" />
            Orari Inverno
          </CardTitle>
        </CardHeader>
        <CardContent>
          {getSchedulesBySeason('inverno').length === 0 ? (
            <p className="text-center text-gray-500 py-4">Nessun orario configurato per l'inverno</p>
          ) : (
            <div className="space-y-6">
              {daysOfWeek.map((day) => {
                const daySchedules = getSchedulesBySeasonAndDay('inverno', day);
                if (daySchedules.length === 0) return null;
                
                return (
                  <div key={day} className="border rounded-lg p-4">
                    <h3 className="font-bold text-lg mb-3 capitalize">{day}</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 px-2">UTA</th>
                            <th className="text-center py-2 px-2">Accensione</th>
                            <th className="text-center py-2 px-2">Spegnimento</th>
                            {canEdit && <th className="text-center py-2 px-2">Azioni</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {daySchedules.map((schedule) => (
                            <tr key={schedule.id} className="border-b hover:bg-gray-50">
                              <td className="py-2 px-2 text-sm">{schedule.uta_name}</td>
                              <td className="py-2 px-2 text-center">
                                <div className="font-medium text-green-600">{schedule.turn_on_time}</div>
                                {schedule.turn_on_time_2 && (
                                  <div className="font-medium text-green-600 text-xs mt-1">{schedule.turn_on_time_2}</div>
                                )}
                              </td>
                              <td className="py-2 px-2 text-center">
                                <div className="font-medium text-red-600">{schedule.turn_off_time}</div>
                                {schedule.turn_off_time_2 && (
                                  <div className="font-medium text-red-600 text-xs mt-1">{schedule.turn_off_time_2}</div>
                                )}
                              </td>
                              {canEdit && (
                                <td className="py-2 px-2">
                                  <div className="flex justify-center gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleEdit(schedule)}
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleDelete(schedule)}
                                      className="text-red-600 hover:text-red-700"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabella Estate */}
      <Card className="shadow-md border-0">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Clock className="w-5 h-5 mr-2 text-orange-600" />
            Orari Estate
          </CardTitle>
        </CardHeader>
        <CardContent>
          {getSchedulesBySeason('estate').length === 0 ? (
            <p className="text-center text-gray-500 py-4">Nessun orario configurato per l'estate</p>
          ) : (
            <div className="space-y-6">
              {daysOfWeek.map((day) => {
                const daySchedules = getSchedulesBySeasonAndDay('estate', day);
                if (daySchedules.length === 0) return null;
                
                return (
                  <div key={day} className="border rounded-lg p-4">
                    <h3 className="font-bold text-lg mb-3 capitalize">{day}</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 px-2">UTA</th>
                            <th className="text-center py-2 px-2">Accensione</th>
                            <th className="text-center py-2 px-2">Spegnimento</th>
                            {canEdit && <th className="text-center py-2 px-2">Azioni</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {daySchedules.map((schedule) => (
                            <tr key={schedule.id} className="border-b hover:bg-gray-50">
                              <td className="py-2 px-2 text-sm">{schedule.uta_name}</td>
                              <td className="py-2 px-2 text-center">
                                <div className="font-medium text-green-600">{schedule.turn_on_time}</div>
                                {schedule.turn_on_time_2 && (
                                  <div className="font-medium text-green-600 text-xs mt-1">{schedule.turn_on_time_2}</div>
                                )}
                              </td>
                              <td className="py-2 px-2 text-center">
                                <div className="font-medium text-red-600">{schedule.turn_off_time}</div>
                                {schedule.turn_off_time_2 && (
                                  <div className="font-medium text-red-600 text-xs mt-1">{schedule.turn_off_time_2}</div>
                                )}
                              </td>
                              {canEdit && (
                                <td className="py-2 px-2">
                                  <div className="flex justify-center gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleEdit(schedule)}
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleDelete(schedule)}
                                      className="text-red-600 hover:text-red-700"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UTASchedules;
